import instance from '@apis/instance';
import { PageResponse } from '@models/page';
import {
    BalanceTransferRequest,
    ChargePrepaidCardRequest,
    DeletePrepaidCardsResponse,
    GetHistoriesParams,
    GetPrepaidCardTransactionsParams,
    GetPrepaidCardTransactionsResponse,
    PrepaidCardGiftBoxResponse,
    PrepaidCardGiftBoxRow,
    PrepaidCardHistoriesResponse,
    PrepaidCardImage,
    PrepaidCardImagesResponse,
    PrepaidCardOrderResponse,
    PrepaidCardResponse,
    PrepaidCardsResponse,
    PurchasePrepaidCardRequest,
    TransferablePrepaidCardsResponse,
    UpdatePrepaidCardNameRequest,
} from '@models/prepaidCard';

class PrepaidCardApi {
    static getPrepaidCard(id: string): Promise<PrepaidCardResponse> {
        return instance.get(`/v0/prepaid-card/${id}`);
    }

    static getPrepaidCards(): Promise<PrepaidCardsResponse> {
        return instance.get('/v0/prepaid-cards');
    }

    static getFavoritePrepaidCards(): Promise<PrepaidCardsResponse> {
        return instance.get('/v0/prepaid-cards/favorites');
    }

    static getTransferableCards(id: string): Promise<TransferablePrepaidCardsResponse> {
        return instance.get(`/v0/prepaid-card/${id}/transferable-cards`);
    }

    static transferBalance(id: string, request: BalanceTransferRequest): Promise<void> {
        return instance.post(`/v0/prepaid-card/${id}/balance-transfer`, request);
    }

    static getHistories(
        id: string,
        params: GetHistoriesParams,
    ): Promise<PrepaidCardHistoriesResponse> {
        return instance.get(`/v0/prepaid-cards/${id}/histories`, { params });
    }

    /** @deprecated Log UI 호환 — 신규는 getHistories 사용 */
    static getPrepaidCardTransactions(
        id: string,
        params: GetPrepaidCardTransactionsParams,
    ): Promise<GetPrepaidCardTransactionsResponse> {
        return instance.get(`/v0/prepaid-card/${id}/transactions`, { params });
    }

    static deletePrepaidCard(id: string): Promise<void> {
        return instance.delete(`/v0/prepaid-card/${id}`);
    }

    static deleteAllPrepaidCards(): Promise<DeletePrepaidCardsResponse> {
        return instance.delete('/v0/prepaid-cards');
    }

    static toggleFavoritePrepaidCard(id: string): Promise<void> {
        return instance.patch(`/v0/prepaid-card/${id}/favorites`);
    }

    static updatePrepaidCardName(
        id: string,
        request: UpdatePrepaidCardNameRequest,
    ): Promise<void> {
        return instance.patch(`/v0/prepaid-card/${id}/name`, request);
    }

    static chargePrepaidCard(
        id: string,
        request: ChargePrepaidCardRequest,
    ): Promise<PrepaidCardOrderResponse> {
        return instance.post(`/v0/prepaid-card/${id}/charge`, request);
    }

    static purchasePrepaidCard(
        request: PurchasePrepaidCardRequest,
    ): Promise<PrepaidCardOrderResponse> {
        return instance.post('/v0/prepaid-card/purchase', request);
    }

    static getPrepaidCardImage(id: string): Promise<PrepaidCardImage> {
        return instance.get(`/v0/prepaid-card/image/${id}`);
    }

    static getPrepaidCardImages(): Promise<PrepaidCardImagesResponse> {
        return instance.get('/v0/prepaid-card/images');
    }

    static cancelPrepaidCardOrder(orderNo: string): Promise<never> {
        return instance.post(`/v0/prepaid-card/order/${orderNo}/cancel`);
    }

    static getGiftBoxCount(): Promise<number> {
        return instance.get<{ count: number }>('/v0/gift-box/cards/count')
            .then((data) => data.count);
    }

    static getGiftBoxes(
        page: number = 0,
        size: number = 10,
    ): Promise<PageResponse<PrepaidCardGiftBoxRow>> {
        return instance.get('/v0/gift-box/cards/received', { params: { page, size } });
    }

    static getGiftBox(id: string): Promise<PrepaidCardGiftBoxResponse> {
        return instance.get(`/v0/gift-box/cards/${id}`);
    }

    static registerGiftBox(id: string): Promise<void> {
        return instance.post(`/v0/gift-box/cards/${id}/register`);
    }
}

export default PrepaidCardApi;
